import * as Sentry from "@sentry/react";
import { useAuth, useClerk } from "@clerk/react";
import { MonyawnProvider, useMonyawn } from "./context/MonyawnContext";
import { AppHeader } from "./components/layout/AppHeader/AppHeader";
import { MobileNavigation } from "./components/layout/MobileNavigation/MobileNavigation";
import { AboutPage } from "./components/pages/About/AboutPage";
import { WorkspacePage } from "./components/pages/Workspace/WorkspacePage";
import { LoginPage } from "./components/pages/Login/LoginPage";
import { StartPage } from "./components/pages/Start/StartPage";
import { ChatIntakePage } from "./components/pages/Intake/ChatIntakePage";
import { SupportPage } from "./components/pages/Support/SupportPage";
import { PrivacyPage } from "./components/pages/Privacy/PrivacyPage";
import { TermsPage } from "./components/pages/Terms/TermsPage";
import { SiteFooter } from "./components/layout/SiteFooter/SiteFooter";
import { CookiesPage } from "./components/pages/Cookies/CookiesPage";
import { SecurityPage } from "./components/pages/Security/SecurityPage";
import { AcceptableUsePage } from "./components/pages/AcceptableUse/AcceptableUsePage";
import { useEffect, useState } from "react";
import {
  defaultAccountDraft,
  defaultUserDraft,
  defaultOpportunityDraft,
  AppPage,
  AccountDraft,
  OpportunityDraft,
  UserDraft,
} from "./context/MonyawnContext.types";
import { AppMode } from "./types";
import { createSeedState } from "./seed";
import { SignalIntakePayload } from "./components/pages/SignalIntake/SignalIntakePage.contract";
import {
  commitMonyawnConversationResult,
  hydrateMonyawnConversationDrafts,
} from "../packages/conversation-adapters/src";

const modeLabels: Record<AppMode, string> = {
  user: "User",
  staff: "Staff",
  admin: "Admin",
};

import { GovernanceBadge } from "./components/panels/GovernanceBadge/GovernanceBadge";
import { useRouteSeo } from "./hooks/useRouteSeo";
import { ConsentBanner } from "./components/legal/ConsentBanner";

type AppContentAuthConfig = {
  enabled: boolean;
  isLoaded: boolean;
  isSignedIn: boolean;
  onContinueLocal: () => void;
  onContinueGoogle: () => void;
  onContinueApple: () => void;
};

function AppContent({ authConfig }: { authConfig: AppContentAuthConfig }) {
  const {
    state,
    patchState,
    currentPage,
    setCurrentPage,
    mobileTab,
    setMobileTab,
    setNotice,
    setMode,
    setSelectedOpportunity,
  } = useMonyawn();

  useRouteSeo(currentPage);

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [accountDraft, setAccountDraft] = useState(defaultAccountDraft);
  const [userDraft, setUserDraft] = useState(defaultUserDraft);
  const [opportunityDraft, setOpportunityDraft] = useState(defaultOpportunityDraft);
  const [, setPendingSignal] = useState<SignalIntakePayload | null>(null);

  const navigateToPage = (page: AppPage) => {
    window.location.hash = page;
    setCurrentPage(page);
  };

  const startNewSignalFlow = () => {
    setAccountDraft(defaultAccountDraft);
    setUserDraft(defaultUserDraft);
    setOpportunityDraft(defaultOpportunityDraft);
    setPendingSignal(null);
    navigateToPage("proof-drop");
  };

  const routeToStart = () => {
    navigateToPage("start");
  };

  const localContinue = authConfig.enabled ? authConfig.onContinueLocal : routeToStart;
  const googleContinue = authConfig.enabled ? authConfig.onContinueGoogle : routeToStart;
  const appleContinue = authConfig.enabled ? authConfig.onContinueApple : routeToStart;

  const showLoginPage = authConfig.enabled ? !authConfig.isSignedIn : currentPage === "login";

  useEffect(() => {
    if (!authConfig.enabled || !authConfig.isLoaded) {
      return;
    }

    if (!authConfig.isSignedIn && currentPage !== "login") {
      navigateToPage("login");
      return;
    }

    if (authConfig.isSignedIn && currentPage === "login") {
      navigateToPage("start");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authConfig.enabled, authConfig.isLoaded, authConfig.isSignedIn, currentPage]);

  const handleLoadMockData = () => {
    patchState(() => createSeedState(), "Mock data loaded.");
    setNotice({ tone: "success", message: "Mock bag loaded. Let’s go." });
    navigateToPage("workspace");
  };

  const handleConversationComplete = ({
    signal,
    accountDraft: nextAccountDraft,
    userDraft: nextUserDraft,
    opportunityDraft: nextOpportunityDraft,
  }: {
    signal: SignalIntakePayload;
    accountDraft: AccountDraft;
    userDraft: UserDraft;
    opportunityDraft: OpportunityDraft;
  }) => {
    const hydratedDrafts = hydrateMonyawnConversationDrafts({
      accountDraft: nextAccountDraft,
      userDraft: nextUserDraft,
      opportunityDraft: nextOpportunityDraft,
      signal,
    });

    setAccountDraft(hydratedDrafts.accountDraft);
    setUserDraft(hydratedDrafts.userDraft);
    setOpportunityDraft(hydratedDrafts.opportunityDraft);
    setPendingSignal(signal);
    const committed = commitMonyawnConversationResult({
      state,
      accountDraft: hydratedDrafts.accountDraft,
      userDraft: hydratedDrafts.userDraft,
      opportunityDraft: hydratedDrafts.opportunityDraft,
      signal,
    });

    if (!committed) {
      setNotice({
        tone: "info",
        message: "Add your name, company, and role before you lock the play.",
      });
      return;
    }

    patchState(() => committed.nextState, committed.patchLabel);
    setPendingSignal(null);
    setOpportunityDraft(defaultOpportunityDraft);
    setNotice({ tone: "success", message: committed.noticeMessage });
    navigateToPage("workspace");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {!showLoginPage && (
        <AppHeader
          currentPage={currentPage}
          currentMode={state.currentMode}
          modeLabel={modeLabels[state.currentMode]}
          lastExportedAt={state.lastExportedAt}
          navigateToPage={navigateToPage}
          onModeChange={(mode: AppMode) => setMode(mode)}
          opportunities={state.opportunities.map((o) => ({
            id: o.opportunity_id,
            name: `${o.company_name} - ${o.role_title}`,
          }))}
          selectedOpportunityId={state.selectedOpportunityId}
          onOpportunitySelect={(id) => setSelectedOpportunity(id)}
        />
      )}

      {windowWidth <= 768 && !showLoginPage && (
        <MobileNavigation
          activeTab={mobileTab}
          onTabChange={(tab) => {
            setMobileTab(tab);
            if (tab === "admin") setMode("admin");
            else setMode("user");
          }}
        />
      )}

      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {showLoginPage && (
        <LoginPage
          onContinueLocal={localContinue}
          onContinueGoogle={googleContinue}
          onContinueApple={appleContinue}
          onLoadMockData={
            import.meta.env.DEV && !authConfig.enabled ? handleLoadMockData : undefined
          }
        />
      )}

      {currentPage === "start" && (
        <StartPage
          onAddSignal={startNewSignalFlow}
          onOpenWorkspace={() => navigateToPage("workspace")}
          onOpenAbout={() => navigateToPage("about")}
        />
      )}

      {(currentPage === "proof-drop" ||
        currentPage === "confirm" ||
        currentPage === "setup-base") && (
        <ChatIntakePage
          accountDraft={accountDraft}
          userDraft={userDraft}
          opportunityDraft={opportunityDraft}
          onBack={routeToStart}
          onCompleteConversation={handleConversationComplete}
        />
      )}

      {currentPage === "workspace" && <WorkspacePage />}

      {currentPage === "about" && (
        <AboutPage
          navigateToPage={navigateToPage}
          RESUME_PATH="/thomas-smith-architect-resume.pdf"
          setNotice={setNotice}
        />
      )}

      {currentPage === "support" && <SupportPage />}
      {currentPage === "privacy" && <PrivacyPage />}
      {currentPage === "terms" && <TermsPage />}
      {currentPage === "cookies" && <CookiesPage />}
      {currentPage === "security" && <SecurityPage />}
      {currentPage === "acceptable-use" && <AcceptableUsePage />}

      <SiteFooter navigateToPage={navigateToPage} />
      <ConsentBanner />
      <GovernanceBadge />
    </div>
  );
}

function AppWithClerk() {
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();

  const openDefaultSignIn = () => {
    void clerk.openSignIn({});
  };

  const openGoogleSignIn = () => {
    void clerk.openSignIn({});
  };

  const openAppleSignIn = () => {
    void clerk.openSignIn({});
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center">
        <div>
          <h1 className="text-xl font-semibold mb-2">Loading secure session...</h1>
          <p className="text-sm text-gray-500">Preparing Clerk authentication.</p>
        </div>
      </div>
    );
  }

  return (
    <MonyawnProvider>
      <AppContent
        authConfig={{
          enabled: true,
          isLoaded,
          isSignedIn: !!isSignedIn,
          onContinueLocal: openDefaultSignIn,
          onContinueGoogle: openGoogleSignIn,
          onContinueApple: openAppleSignIn,
        }}
      />
    </MonyawnProvider>
  );
}

function AppWithoutClerk() {
  return (
    <MonyawnProvider>
      <AppContent
        authConfig={{
          enabled: false,
          isLoaded: true,
          isSignedIn: false,
          onContinueLocal: () => undefined,
          onContinueGoogle: () => undefined,
          onContinueApple: () => undefined,
        }}
      />
    </MonyawnProvider>
  );
}

export default function App({ clerkEnabled = false }: { clerkEnabled?: boolean }) {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8 text-center">
          <div>
            <h1 className="text-xl font-semibold mb-2">Something went wrong.</h1>
            <p className="text-sm text-gray-500 mb-4">
              The error has been reported. Refresh to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-black text-white rounded text-sm"
            >
              Refresh
            </button>
          </div>
        </div>
      }
    >
      {clerkEnabled ? <AppWithClerk /> : <AppWithoutClerk />}
    </Sentry.ErrorBoundary>
  );
}
